import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

import type { SettingsAdministration } from './types';
import type { ChatSettingsSection } from '../../contexts/chat-overlay-context';

interface SettingsNavigationHint {
  userUuid: string;
  showTeams: boolean;
  showAdmins: boolean;
}

export function settingsNavigationStorageKey(userUuid: string) {
  return `nova:settings-admin-navigation:v1:${encodeURIComponent(userUuid)}`;
}

/** Remember navigation only. Group data and manager membership always come from the current BFF request. */
export function useSettingsNavigation({
  enabled,
  userUuid,
  session,
  settingsSection,
  status,
  showTeams,
  showAdmins,
}: SettingsNavigationHint & {
  enabled: boolean;
  session: object;
  settingsSection: ChatSettingsSection;
  status: SettingsAdministration['status'];
}) {
  const [stored, setStored, removeStored] = useLocalStorage<SettingsNavigationHint>(
    settingsNavigationStorageKey(userUuid),
  );
  // useLocalStorage refreshes changed keys in a layout effect; never use the previous user's value in the meantime.
  const hint =
    stored?.userUuid === userUuid && typeof stored.showTeams === 'boolean' && typeof stored.showAdmins === 'boolean'
      ? stored
      : undefined;
  const rememberedTeams = !!hint?.showTeams;
  const rememberedAdmins = !!hint?.showAdmins;

  useEffect(() => {
    if (!enabled || status !== 'ready') return;
    // An error is not a confirmed loss of access. Preserve the last successful hint for retries.
    if (!showTeams && !showAdmins) removeStored();
    else if (showTeams !== rememberedTeams || showAdmins !== rememberedAdmins) {
      setStored({ userUuid, showTeams, showAdmins });
    }
  }, [enabled, status, showTeams, showAdmins, rememberedTeams, rememberedAdmins, userUuid, setStored, removeStored]);

  const visibleTeams = showTeams || (status !== 'ready' && rememberedTeams);
  const visibleAdmins = showAdmins || (status !== 'ready' && rememberedAdmins);
  const [visit, setVisit] = useState<{ session: object; section: 'teams' | 'admins' } | null>(null);

  // A selected destination may finish empty. Keep it until the user leaves, without retaining any group data.
  const selectedSection =
    enabled && (settingsSection === 'teams' || settingsSection === 'admins') ? settingsSection : null;
  const retained = visit?.session === session && visit.section === selectedSection ? visit.section : null;
  // Adjust visit state before rendering children, so a new section or identity cannot retain an old page.
  if (selectedSection && !retained && (selectedSection === 'teams' ? visibleTeams : visibleAdmins)) {
    setVisit({ session, section: selectedSection });
  } else if (visit && !retained) {
    setVisit(null);
  }
  return {
    showTeams: enabled && (visibleTeams || retained === 'teams'),
    showAdmins: enabled && (visibleAdmins || retained === 'admins'),
    hasNavigationHint: enabled && (rememberedTeams || rememberedAdmins),
  };
}
